import { useAvailableTenants } from '@/api/hooks/useAvailableTenants'
import { useOrderDashboardMappingMetrics } from '@/api/hooks/useOrderDashboard'
import { ObjectDisplay } from '@/components/Display'
import { FiltersAppliedButton } from '@/components/Display/FiltersAppliedButton'
import { useObject } from '@/contexts/ObjectContext'
import { useDashboardNavigation } from '@/pages/Dashboard/hooks/useDashboardNavigation'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { BBDTag, Horizontal } from '@gravitate-js/excalibrr'
import { Radio } from 'antd'
import { useCallback } from 'react'

export function ReferenceDataFilter() {
  const { selectedObject, setSelectedObject, availableObjects } = useObject()
  const { isLoadingTenants } = useAvailableTenants()

  // Memoize the callback to prevent infinite loops
  const handleObjectTypeFromUrl = useCallback(
    (type: string | null) => {
      setSelectedObject(type)
    },
    [setSelectedObject],
  )

  // URL navigation state management
  const { filters, clearFilters } = useDashboardNavigation({
    initialTab: 'dashboard',
    availableObjectValues: availableObjects.map((obj) => obj.value),
    onObjectTypeFromUrl: handleObjectTypeFromUrl,
  })

  // Handle clear filters - also select first object
  const handleClearFilters = useCallback(() => {
    clearFilters()
    const firstObject = availableObjects[0]
    if (firstObject) {
      setSelectedObject(firstObject.value)
    }
  }, [clearFilters, availableObjects, setSelectedObject])

  // Server-computed mapping coverage, keyed by mapping type. Shared with the
  // orders dashboard's Reference Data card: same query, same 24h staleTime, so
  // whichever page loads first pays for it.
  const { data: mappingStats } = useOrderDashboardMappingMetrics(
    !isLoadingTenants,
  )

  return (
    <Horizontal gap={12} alignItems='center'>
      {filters.hasActiveFilters && (
        <FiltersAppliedButton
          onClearFilters={handleClearFilters}
          tooltipContent={
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                Active Filters:
              </div>
              {filters.sourceIds && <div>Source IDs: {filters.sourceIds}</div>}
              {filters.goIds && <div>GO IDs: {filters.goIds}</div>}
            </div>
          }
        />
      )}
      <Radio.Group
        size='middle'
        value={selectedObject?.value ?? 'all'}
        onChange={(e) =>
          setSelectedObject(e.target.value === 'all' ? null : e.target.value)
        }
        buttonStyle='solid'
      >
        {availableObjects.map((obj) => {
          const stats = mappingStats?.[obj.mapping_type]
          const isSelected = selectedObject?.value === obj.value
          // `needs_mapping_percentage` is mapped over mapped-plus-requested,
          // which is what /mappings/percentages used to return here.
          const tagConfig = stats
            ? getTagPropsForPercentage(
                stats.needs_mapping_percentage,
                isSelected,
              )
            : null

          return (
            <Radio.Button key={obj.value} value={obj.value}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  height: '100%',
                }}
              >
                <ObjectDisplay
                  objectType={obj.value}
                  iconSize={14}
                  iconColor={isSelected ? 'white' : undefined}
                  label={obj.label}
                  textStyle={isSelected ? { color: 'white' } : undefined}
                />
                {stats && tagConfig && (
                  <BBDTag {...tagConfig.props} style={{ lineHeight: 1 }}>
                    <Horizontal gap={4} alignItems='center'>
                      {tagConfig.icon}
                      <span style={{ lineHeight: 1 }}>
                        {stats.needs_mapping_percentage}%
                      </span>
                    </Horizontal>
                  </BBDTag>
                )}
              </div>
            </Radio.Button>
          )
        })}
      </Radio.Group>
    </Horizontal>
  )
}

// Helper function to get tag props and icon based on percentage
function getTagPropsForPercentage(percentage: number, isSelected: boolean) {
  if (isSelected) {
    // No colorization when selected
    return { props: {}, icon: null }
  }

  if (percentage === 100) {
    return {
      props: { success: true },
      icon: <CheckCircleOutlined />,
    }
  } else if (percentage >= 90) {
    return {
      props: { warning: true },
      icon: <WarningOutlined />,
    }
  } else {
    return {
      props: { error: true },
      icon: <CloseCircleOutlined />,
    }
  }
}
